// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  BaixaParcelaPagarDialog.tsx
//
//  Pagamento de UMA parcela (espelho de BaixaParcelaDialog do Contas a Receber).
//  O valor vem preenchido com o saldo da parcela; reduzi-lo faz uma baixa
//  PARCIAL — a parcela continua aberta com o remanescente, como no desktop.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useFormasPagamentoCombo } from '../hooks/useCadastrosFinanceiros';
import type { ContaPagarParcelaDto, BaixarParcelaPagarRequest } from '../types';

// Radix Select não aceita value="" (string vazia derruba o diálogo) — sentinela
// para "sem forma informada".
const SEM_FORMA = '__none__';

interface BaixaParcelaPagarDialogProps {
  parcela: ContaPagarParcelaDto | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (parcelaId: number, req: BaixarParcelaPagarRequest) => void;
  isLoading?: boolean;
}

const hoje = () => new Date().toISOString().slice(0, 10);
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function BaixaParcelaPagarDialog({
  parcela, onOpenChange, onConfirm, isLoading = false,
}: BaixaParcelaPagarDialogProps) {
  const [dtPagamento, setDtPagamento]       = useState(hoje());
  const [valorPago, setValorPago]           = useState('');
  const [valorDesconto, setValorDesconto]   = useState('');
  const [valorAcrescimo, setValorAcrescimo] = useState('');
  const [formaPagamento, setFormaPagamento] = useState<string>(SEM_FORMA);

  // Só formas que permitem PAGAMENTO (a mesma tabela serve receber e pagar).
  const { formasPagamento } = useFormasPagamentoCombo();
  const formasPagar = formasPagamento.filter((f) => f.permitePagamento);

  // Reabre sempre com o saldo cheio da parcela corrente.
  useEffect(() => {
    if (parcela) {
      setDtPagamento(hoje());
      setValorPago(String(parcela.saldo));
      setValorDesconto('');
      setValorAcrescimo('');
      setFormaPagamento(SEM_FORMA);
    }
  }, [parcela]);

  if (!parcela) return null;

  const pago      = Number(valorPago) || 0;
  const desconto  = Number(valorDesconto) || 0;
  const acrescimo = Number(valorAcrescimo) || 0;
  const restante  = Math.max(0, parcela.saldo - pago - desconto + acrescimo);
  const parcial   = restante > 0;

  function handleConfirm() {
    onConfirm(parcela!.id, {
      dtPagamento,
      valorPago: pago,
      valorDesconto: desconto || undefined,
      valorAcrescimo: acrescimo || undefined,
      formaPagamentoId: formaPagamento === SEM_FORMA ? undefined : Number(formaPagamento),
    });
  }

  return (
    <Dialog open={!!parcela} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Pagar parcela {parcela.nroParcela}/{parcela.qtdParcelas}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fornecedor</span>
              <span className="font-medium">
                {parcela.fornecedorNome || `Forn. #${parcela.fornecedorId}`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Vencimento</span>
              <span>{new Date(parcela.dtVencimento).toLocaleDateString('pt-BR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Saldo da parcela</span>
              <span className="font-semibold">{brl(parcela.saldo)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="dtPagamentoParcelaPagar">Data do pagamento</Label>
              <Input
                id="dtPagamentoParcelaPagar"
                type="date"
                value={dtPagamento}
                onChange={(e) => setDtPagamento(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="valorPagoParcelaPagar">Valor pago</Label>
              <Input
                id="valorPagoParcelaPagar"
                type="number"
                step="0.01"
                min="0.01"
                value={valorPago}
                onChange={(e) => setValorPago(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="descontoParcelaPagar">Desconto</Label>
              <Input
                id="descontoParcelaPagar"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={valorDesconto}
                onChange={(e) => setValorDesconto(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="acrescimoParcelaPagar">Acréscimo (juros/multa)</Label>
              <Input
                id="acrescimoParcelaPagar"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={valorAcrescimo}
                onChange={(e) => setValorAcrescimo(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="formaPagamentoParcelaPagar">Forma de pagamento</Label>
            <Select value={formaPagamento} onValueChange={setFormaPagamento}>
              <SelectTrigger id="formaPagamentoParcelaPagar">
                <SelectValue placeholder="Selecione (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SEM_FORMA}>— Nenhuma —</SelectItem>
                {formasPagar.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>{f.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {parcial && pago > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
              <span>
                Pagamento <strong>parcial</strong>: restam <strong>{brl(restante)}</strong> nesta
                parcela, que continuará em aberto.
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !dtPagamento || pago <= 0}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {parcial && pago > 0 ? 'Pagar parcial' : 'Confirmar pagamento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
